import { useMemo, useState } from 'react';
import { contaSedute } from '@leopart/calc';
import { ITrainingBlock, ITrainingCategory } from '../../../models/allenamenti';
import { FALLBACK_CATEGORY_COLOR } from '../allenamenti.constants';
import { subcategorieDelleMacro, sottoAmmesse } from '../taxonomy.utils';
import { DistributionSlice } from '../../../shared/charts/chart.types';

/** Opzione per i menu multi-select del filtro. */
export interface Option {
  label: string;
  value: string;
}

/** Una riga del report per categoria/sottocategoria selezionata. */
export interface IReportRow {
  slug: string;
  label: string;
  color: string;
  /** true = sottocategoria, false = macro-categoria. */
  isSub: boolean;
  /** Minuti totali dei blocchi che contengono lo slug (multi-categoria: interi). */
  durata: number;
  /** Giorni distinti in cui lo slug compare — «quante volte usata» (sedute). */
  sedute: number;
  /** Minuti medi per seduta (`durata / sedute`, arrotondati). */
  media: number;
}

/**
 * Filtro dell'analisi allenamenti (feature #14): selezione di categorie e/o
 * sottocategorie che restringe la tabella «Blocchi del periodo» e alimenta un
 * report (durata totale, media/seduta, sedute) + mini grafico.
 *
 * Tutto client-side sui blocchi grezzi già scaricati (`GET /training-blocks`
 * non è paginato): i numeri per categoria sono indipendenti dal filtro (un
 * blocco multi-categoria conta i minuti interi su ogni categoria, decisione
 * 2026-08-18), quindi non c'è nulla da ricalcolare lato server e le «sedute»
 * distinte e le sottocategorie non-fisiche — assenti nell'aggregato server —
 * si ottengono solo da qui.
 */
export const useAnalysisFilter = (
  blocks: ITrainingBlock[],
  categories: ITrainingCategory[],
  bySlug: Map<string, { labelIt: string; color: string }>,
  periodTotalMinutes: number,
) => {
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [subcategoryIds, setSubcategoryIds] = useState<string[]>([]);

  const categoryOptions: Option[] = useMemo(
    () => categories.map((c) => ({ label: c.labelIt, value: c.slug })),
    [categories],
  );

  // Sottocategorie selezionabili = unione delle sotto delle macro scelte, col
  // prefisso della macro (come useBlockForm): senza, con più macro l'elenco
  // diventa un blocco di nomi senza padrone.
  const subcategoryOptions: Option[] = useMemo(
    () =>
      subcategorieDelleMacro(categories, categoryIds).map((s) => ({
        label: s.labelIt,
        value: s.slug,
      })),
    [categories, categoryIds],
  );

  /** Cambio categorie: pota le sotto non più ammesse dalle macro scelte. */
  const setCategories = (ids: string[]) => {
    setCategoryIds(ids);
    const ammesse = sottoAmmesse(categories, ids);
    setSubcategoryIds((prev) => prev.filter((s) => ammesse.has(s)));
  };

  const selectedSlugs = useMemo(
    () => [...categoryIds, ...subcategoryIds],
    [categoryIds, subcategoryIds],
  );
  const isFiltering = selectedSlugs.length > 0;

  const filteredBlocks = useMemo(() => {
    if (!isFiltering) return blocks;
    const sel = new Set(selectedSlugs);
    return blocks.filter(
      (b) =>
        (b.categoryIds ?? []).some((c) => sel.has(c)) ||
        (b.subcategoryIds ?? []).some((s) => sel.has(s)),
    );
  }, [blocks, selectedSlugs, isFiltering]);

  const reportRows: IReportRow[] = useMemo(() => {
    if (!isFiltering) return [];
    const subSet = new Set(subcategoryIds);
    return selectedSlugs
      .map((slug) => {
        const isSub = subSet.has(slug);
        // Ogni riga guarda TUTTI i blocchi che contengono quello slug (non solo
        // i filtrati): il numero per categoria è indipendente dalla selezione.
        const matching = blocks.filter((b) =>
          (isSub ? b.subcategoryIds ?? [] : b.categoryIds ?? []).includes(slug),
        );
        const durata = matching.reduce((sum, b) => sum + b.durationMinutes, 0);
        // contaSedute (@leopart/calc) = giorno + fascia: stessa formula del
        // backend, regge il giorno del doppio allenamento (ADR 025). Non
        // reintrodurre `new Set(localDate).size`, che lì conterebbe una seduta.
        const sedute = contaSedute(matching);
        const info = bySlug.get(slug);
        return {
          slug,
          label: info?.labelIt ?? slug,
          color: info?.color ?? FALLBACK_CATEGORY_COLOR,
          isSub,
          durata,
          sedute,
          media: sedute ? Math.round(durata / sedute) : 0,
        };
      })
      .sort((a, b) => b.durata - a.durata);
  }, [blocks, selectedSlugs, subcategoryIds, isFiltering, bySlug]);

  // Percentuale come CategoryBars: quota del tempo TOTALE di periodo toccata
  // dallo slug (può sommare oltre 100 tra righe — barre, non torta).
  const chartSlices: DistributionSlice[] = useMemo(
    () =>
      reportRows.map((r) => ({
        id: r.slug,
        label: r.label,
        value: r.durata,
        percent: periodTotalMinutes
          ? Math.round((r.durata / periodTotalMinutes) * 100)
          : null,
        color: r.color,
      })),
    [reportRows, periodTotalMinutes],
  );

  const clear = () => {
    setCategoryIds([]);
    setSubcategoryIds([]);
  };

  return {
    categoryIds,
    subcategoryIds,
    setCategories,
    setSubcategoryIds,
    categoryOptions,
    subcategoryOptions,
    isFiltering,
    filteredBlocks,
    reportRows,
    chartSlices,
    clear,
  };
};
